/**
 * Shared sort controls used by every standalone configuration list page
 * (App Configuration, PPPC, …).
 *
 * Renders a `<Select>` for the sort field next to a direction toggle button.
 * The available fields and their labels are passed in by the caller because
 * each surface exposes a slightly different set (e.g. PPPC has "Identifier"
 * instead of "App name"), but the wrapper layout, spacing, ARIA labels and
 * direction-toggle behaviour are identical everywhere.
 */
import { ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type SortDirection = "asc" | "desc";

export interface SortOption<TField extends string = string> {
  value: TField;
  label: string;
}

interface SortControlsProps<TField extends string> {
  field: TField;
  direction: SortDirection;
  options: ReadonlyArray<SortOption<TField>>;
  onFieldChange: (field: TField) => void;
  onDirectionChange: (direction: SortDirection) => void;
  /** Override the trigger width if needed; defaults to w-44 to match filters. */
  triggerClassName?: string;
}

export function SortControls<TField extends string>({
  field,
  direction,
  options,
  onFieldChange,
  onDirectionChange,
  triggerClassName = "w-44",
}: SortControlsProps<TField>) {
  return (
    <>
      <Select value={field} onValueChange={(v) => onFieldChange(v as TField)}>
        <SelectTrigger className={triggerClassName} aria-label="Sort by">
          <span className="text-muted-foreground mr-1.5 text-xs">Sort:</span>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        variant="outline"
        size="icon"
        onClick={() =>
          onDirectionChange(direction === "asc" ? "desc" : "asc")
        }
        aria-label={
          direction === "asc"
            ? "Sort direction: ascending. Click to switch to descending."
            : "Sort direction: descending. Click to switch to ascending."
        }
        title={direction === "asc" ? "Ascending" : "Descending"}
      >
        {direction === "asc" ? (
          <ArrowUp className="h-4 w-4" />
        ) : (
          <ArrowDown className="h-4 w-4" />
        )}
      </Button>
    </>
  );
}
